import React, { type ReactNode, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import type { RacePageDTO, RaceStatus } from '@mobi/api-types'
import type { BetSource } from '@mobi/betslip/types'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { font, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import { colors } from '@mobi/component-library/Foundations/colors'
import { hexColors } from '@mobi/settings/hexColors'
import { BettableTips } from '@core/Areas/Racing/Components/BettableTips/BettableTips'
import type { RawTips } from '@core/Areas/Racing/Components/BettableTips/types'
import { PreviewParagraphs } from './Components/PreviewParagraphs'
import { previewTipsTracker } from './helpers/analytics'

const enum LocalConstants {
  ReadMoreLabel = 'Read More',
  ReadLessLabel = 'Read Less',
  collapsedPreviewHeight = '3.5rem',
}

type Props = {
  title: ReactNode
  author?: string
  headerLeadContent?: ReactNode
  headerTrailContent?: ReactNode
  tips: RawTips
  tipSource: BetSource
  isRaceOpen: boolean
  raceKey: string
  race?: RacePageDTO
  previewText?: string
  suggestedBet?: string
  panelTestId?: string
  containerTestId?: string
}

export const RacePreviewTipsCard = ({
  title,
  author,
  headerLeadContent,
  headerTrailContent,
  tips,
  tipSource,
  isRaceOpen,
  raceKey,
  race,
  previewText,
  suggestedBet,
  panelTestId,
  containerTestId,
}: Props): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false)

  const hasTips = Array.isArray(tips) ? tips.length > 0 : !!tips

  const previewParagraphs = useMemo(
    () => <PreviewParagraphs text={previewText ?? ''} />,
    [previewText]
  )

  const hasPreview = !!previewText

  return (
    <WrapperStyled data-testid={panelTestId}>
      <div data-testid={containerTestId}>
        <div>
          {headerLeadContent}
          <strong>{title}</strong>
          {headerTrailContent}
        </div>

        <div>
          {hasTips && (
            <div className='preview-tips__bettable-tips-container'>
              <BettableTips
                acceptorsLimit={4}
                tips={tips}
                isRaceOpen={isRaceOpen}
                raceKey={raceKey}
                race={race}
                tipSource={tipSource}
                author={author}
              />
            </div>
          )}

          {hasPreview && (
            <div className={hasTips ? 'preview-tips__has-tips' : ''}>
              <div
                className={
                  'race-preview-tips__preview-text' + (isExpanded ? '' : ' preview-tips__collapsed')
                }
                data-testid='new-race-content-preview-text'
              >
                {previewParagraphs}
                <span
                  className={
                    `race-preview-tips__readmore-btn` +
                    (isExpanded ? ' preview-tips__expanded' : ' preview-tips__collapsed')
                  }
                  role='button'
                  aria-expanded={isExpanded}
                  onClick={() => {
                    setIsExpanded(prev => !prev)
                    if (!isExpanded) {
                      previewTipsTracker({
                        interaction_type: 'Read More',
                        author: author || '',
                        raceCode: race?.MeetingInformation?.MeetingCode || '',
                        meetingName: race?.MeetingInformation?.MeetingName || '',
                        raceStatus: race?.MeetingInformation?.SelectedRace
                          ?.RaceStatus as RaceStatus,
                      })
                    }
                  }}
                >
                  {!isExpanded && <span>...</span>}{' '}
                  <span>
                    {isExpanded ? LocalConstants.ReadLessLabel : LocalConstants.ReadMoreLabel}
                  </span>
                </span>
              </div>
            </div>
          )}

          {!!suggestedBet && (
            <div className='race-preview-tips__suggested-bet'>
              <div>
                <Icon size='1.6rem' name='SolidThumbsUp' color={colors.warning[600]} />
                <strong>Best Bet:</strong>
              </div>
              <span>{suggestedBet}</span>
            </div>
          )}
        </div>
      </div>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  paddingBottom: spacing.sm,
  paddingTop: spacing.sm,

  '> div': {
    borderRadius: radius.lgx3,
    overflow: 'hidden',
    border: `0.1rem solid ${hexColors.veryLightGrey}`,
    boxShadow: shadow.md,

    // Header
    '> div:first-of-type': {
      borderRadius: `${radius.lgx3} ${radius.lgx3} 0 0`,
      background: colors.studio[700],
      color: colors.white,
      display: 'flex',
      alignItems: 'center',
      padding: `${spacing.smx3} ${spacing.sm}`,
      height: '2.6rem',
      fontFamily: font.family.primary,

      '> strong': {
        flex: '1 1 0%',
        fontSize: font.size.md.fontSize,
        lineHeight: font.size.md.lineHeight,
        color: colors.white,
        fontWeight: font.weight.semibold,
      },
    },

    // Card Body
    '> div:last-of-type': {
      padding: `${spacing.smx1} ${spacing.sm}`,
      backgroundColor: colors.white,

      [`div.preview-tips__has-tips`]: {
        display: 'flex',
        paddingTop: spacing.sm,
      },

      // Tips Section
      '> div': {
        display: 'flex',
        flexDirection: 'column',

        '> div:first-of-type': {
          position: 'relative',
          margin: 0,
          fontSize: font.size.md.fontSize,
          lineHeight: font.size.md.lineHeight,
          color: hexColors.nero,
        },
      },

      '.race-preview-tips__readmore-btn': {
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: font.size.md.fontSize,
        lineHeight: font.size.md.lineHeight,

        '> span:first-of-type': {
          color: colors.black,
        },

        '> span:last-of-type': {
          color: colors.studio[500],
          fontWeight: 'semiBold',
        },

        ':hover > span:last-of-type': {
          textDecoration: 'underline',
        },
      },

      '.race-preview-tips__readmore-btn.preview-tips__collapsed': {
        position: 'absolute',
        bottom: 0,
        right: 0,
        background: colors.white,
        paddingLeft: '0.4rem',
      },

      '.race-preview-tips__readmore-btn.preview-tips__expanded': {
        position: 'static',
        padding: '0.4rem 0 0',
        background: 'none',
      },

      '.race-preview-tips__preview-text': {
        position: 'relative',
        margin: 0,
        fontSize: font.size.md.fontSize,
        lineHeight: font.size.md.lineHeight,
        color: hexColors.nero,
        fontFamily: font.family.primary,
      },

      '.race-preview-tips__preview-text.preview-tips__collapsed': {
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },

      'div.race-preview-tips__suggested-bet': {
        marginTop: spacing.smx1,
        paddingTop: spacing.smx1,
        borderTop: `0.1rem solid ${hexColors.veryLightGrey}`,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.smx1,
        flexWrap: 'wrap',
        fontFamily: font.family.primary,

        '> div': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',

          '> strong': {
            fontSize: font.size.md.fontSize,
            lineHeight: font.size.md.lineHeight,
            color: colors.warning[600],
            marginLeft: spacing.smx2,
            fontWeight: font.weight.semibold,
          },
        },

        '> span': {
          fontSize: font.size.md.fontSize,
          color: colors.neutral[900],
          lineHeight: font.size.md.lineHeight,
        },
      },
    },
  },
})
